import { useState } from 'react'
import { Clock, Copy, Check } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import type { WorkingHours, WorkingHoursDay } from '@/hooks/useAssistantCalendarSettings'

interface WorkingHoursEditorProps {
  value: WorkingHours
  onChange: (hours: WorkingHours) => void
  disabled?: boolean
}

const DAY_LABELS: Record<keyof WorkingHours, string> = {
  monday: 'Segunda-feira',
  tuesday: 'Terca-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sabado',
  sunday: 'Domingo'
}

const DAY_ORDER: (keyof WorkingHours)[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
]

export function WorkingHoursEditor({ value, onChange, disabled }: WorkingHoursEditorProps) {
  const [copySource, setCopySource] = useState<keyof WorkingHours | null>(null)

  const updateDay = (day: keyof WorkingHours, updates: Partial<WorkingHoursDay>) => {
    onChange({
      ...value,
      [day]: { ...value[day], ...updates }
    })
  }

  const copyToAll = (sourceDay: keyof WorkingHours) => {
    const source = value[sourceDay]
    const newHours: WorkingHours = { ...value }

    DAY_ORDER.forEach(day => {
      if (day !== sourceDay) {
        newHours[day] = { ...source }
      }
    })

    onChange(newHours)
    setCopySource(sourceDay)
    setTimeout(() => setCopySource(null), 2000)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-[#334155] dark:text-[#D4D4D8]">
        <Clock className="w-4 h-4" />
        Horarios de Funcionamento
      </div>

      <div className="space-y-2">
        {DAY_ORDER.map(day => (
          <div
            key={day}
            className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] dark:border-[#3F3F464D] dark:bg-[#18181B]/50"
          >
            <Switch
              checked={value[day].enabled}
              onCheckedChange={(checked) => updateDay(day, { enabled: checked })}
              disabled={disabled}
            />

            <span className="w-28 text-sm text-[#334155] dark:text-[#D4D4D8]">
              {DAY_LABELS[day]}
            </span>

            {value[day].enabled ? (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={value[day].start}
                    onChange={(e) => updateDay(day, { start: e.target.value })}
                    disabled={disabled}
                    className="px-2 py-1 text-sm rounded-lg border border-[#E5E7EB] bg-white text-[#0F172A] focus:outline-none focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]"
                  />
                  <span className="text-[#64748B] dark:text-[#71717A]">ate</span>
                  <input
                    type="time"
                    value={value[day].end}
                    onChange={(e) => updateDay(day, { end: e.target.value })}
                    disabled={disabled}
                    className="px-2 py-1 text-sm rounded-lg border border-[#E5E7EB] bg-white text-[#0F172A] focus:outline-none focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => copyToAll(day)}
                  disabled={disabled}
                  className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-[#64748B] dark:text-[#A1A1AA] hover:text-[#16A34A] dark:hover:text-[#22C55E] transition-colors disabled:opacity-50"
                  title="Copiar para todos os dias"
                >
                  {copySource === day ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">Copiar</span>
                </button>
              </>
            ) : (
              <span className="text-sm text-[#64748B] dark:text-[#71717A] italic">Fechado</span>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-[#64748B] dark:text-[#71717A]">
        O assistente so podera agendar dentro destes horarios.
      </p>
    </div>
  )
}
